import React from 'react';

import { observable } from 'mobx';

import {
  Optional,
  OrderedMap,
  ServiceStatus,
} from 'services/base/types';

import {
  CachedNetworkThroughput,
  HostMap,
  IPangolinDomain,
  IPangolinDomains,
  Issue,
  IssueMap,
  IssuesFilter,
  NetworkCounters,
  NetworkThroughputDuration,
  History,
  HistoryMap,
  HistoryFilter,
  ScheduleMap,
  UpdateSnapshotMap,
  UpdateReleaseMap,
  UpdateUpgradeMap,
  UpdateRestoreMap,
  UpdateHistoryRefMap,
  VPNServer,
  VPNClientMap,
} from './types';

export class PangolinDomain implements IPangolinDomain {
  id: string;

  @observable.deep
  hosts: HostMap;

  @observable.deep
  issues: IssueMap;

  @observable.ref
  issuesItem: Optional<Issue>;

  @observable.ref
  issuesOffset: number;

  @observable.ref
  issuesCapped: boolean;

  @observable.ref
  issuesFilter: IssuesFilter;

  @observable.ref
  issuesFiltered: boolean;

  @observable.ref
  issuesStatus: ServiceStatus;

  @observable.ref
  history: HistoryMap;

  @observable.ref
  historyItem: Optional<History>;

  @observable.ref
  historyOffset: number;

  @observable.ref
  historyCapped: boolean;

  @observable.ref
  historyFilter: HistoryFilter;

  @observable.ref
  historyFiltered: boolean;

  @observable.ref
  historyStatus: ServiceStatus;

  @observable.deep
  schedules: ScheduleMap;

  @observable.deep
  updateReleases: UpdateReleaseMap;

  @observable.deep
  updateSnapshots: UpdateSnapshotMap;

  @observable.deep
  updateUpgrades: UpdateUpgradeMap;

  @observable.deep
  updateRestores: UpdateRestoreMap;

  @observable.deep
  updateHistory: UpdateHistoryRefMap;

  @observable.deep
  vpnServer: VPNServer;

  @observable.deep
  vpnClients: VPNClientMap;

  @observable.deep
  throughput: Record<NetworkThroughputDuration, CachedNetworkThroughput>;

  // This marks whether any request to a throughput endpoint has worked.
  // If at least one has worked on this Pangolin, then we know that it has
  // received the throughput update and we can start showing the loading icon.
  // FRESH means unknown.
  // FAILED means unavailable.
  // LOADED means available.
  @observable.ref
  throughputEnabled: ServiceStatus;

  counters: Optional<NetworkCounters>;

  constructor(id: string) {
    const now = new Date();
    this.id = id;
    this.hosts = new HostMap();
    this.issues = new IssueMap();
    this.issuesItem = undefined;
    this.issuesOffset = 0;
    this.issuesCapped = false;
    this.issuesFilter = {
      search: '',
      start: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1,
                      now.getHours(), now.getMinutes(), now.getSeconds()),
      end: new Date(now.getFullYear(), now.getMonth(), now.getDate(),
                    now.getHours(), now.getMinutes(), now.getSeconds()),
      seen: { seen: true, unseen: true },
      severityMin: 1.0, severityMax: 5.0,
      affectedMin: 1.0, affectedMax: 5.0,
      incidentsMin: 1.0, incidentsMax: 5.0,
      kinds: [], devices: [],
    };
    this.issuesFiltered = false;
    this.issuesStatus = ServiceStatus.FRESH;
    this.history = new HistoryMap();
    this.historyItem = undefined;
    this.historyOffset = 0;
    this.historyCapped = false;
    this.historyFilter = {
      start: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1,
                      now.getHours(), now.getMinutes(), now.getSeconds()),
      end: new Date(now.getFullYear(), now.getMonth(), now.getDate(),
                    now.getHours(), now.getMinutes(), now.getSeconds()),
      directions: { outbound: true, inbound: true, local: true },
      actions: { allowed: true, blocked: true },
      protocols: [], sites: [],
      devices: [], locations: [],
    };
    this.historyFiltered = false;
    this.historyStatus = ServiceStatus.FRESH;
    this.schedules = new ScheduleMap();
    this.updateReleases = new UpdateReleaseMap();
    this.updateSnapshots = new UpdateSnapshotMap();
    this.updateUpgrades = new UpdateUpgradeMap();
    this.updateRestores = new UpdateRestoreMap();
    this.updateHistory = new UpdateHistoryRefMap();
    this.vpnServer = { isEnabled: false };
    this.vpnClients = new VPNClientMap();
    this.throughput = {
      [NetworkThroughputDuration.HOURLY]: { status: ServiceStatus.FRESH },
      [NetworkThroughputDuration.DAILY]: { status: ServiceStatus.FRESH },
      [NetworkThroughputDuration.WEEKLY]: { status: ServiceStatus.FRESH },
    };
    this.throughputEnabled = ServiceStatus.FRESH;
    this.counters = undefined;
  }
}

export class PangolinDomains implements IPangolinDomains {
  @observable.deep
  domains: OrderedMap<string, PangolinDomain>;

  constructor() {
    this.domains = new OrderedMap();
  }
}

export const GlobalPangolinDomains: IPangolinDomains = new PangolinDomains();

export const PangolinDomainsContext: React.Context<IPangolinDomains> = React.createContext(GlobalPangolinDomains);
export const PangolinDomainsProvider = PangolinDomainsContext.Provider;
export const PangolinDomainsConsumer = PangolinDomainsContext.Consumer;

export function usePangolinDomains(): IPangolinDomains {
  return React.useContext(PangolinDomainsContext);
}

export function usePangolinDomain(id: string): Optional<IPangolinDomain> {
  return usePangolinDomains().domains.get(id);
}
